import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, MapPin, Phone, Mail, Building2, TrendingUp, Award, Calendar, CheckCircle, AlertCircle, Plus, Upload } from 'lucide-react';

const ProviderDirectory = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    practice: '',
    address: '',
    specialty: 'General Dentist',
    birthday: '',
    notes: ''
  });

  // Original provider data from CSV as fallback
  const defaultProviders = [
    { id: 1, name: 'Dr. Gerard Nikodem', phone: '(636) 937-9133', email: 'gcniko@aol.com', practice: 'Gerard Nikodem', address: '1121 West Gannon Drive, Festus, Missouri, 63028', specialty: 'General Dentist', referral_count: 237, birthday: 'May 4th', last_referral: '10/22/2025', claimed: false },
    { id: 2, name: 'Dr. Monya Phung', phone: '(636) 931-7766', email: 'rplimpi@gmail.com', practice: 'Festus Family Smiles', address: '1518 Parkway West, Festus, Missouri, 63028', specialty: 'General Dentist', referral_count: 174, birthday: 'Feb 13th', last_referral: '11/05/2025', claimed: true },
    { id: 3, name: 'Dr. David Schertzer', phone: '(314) 842-5177', email: 'info@kennerlydental.com', practice: 'Kennerly Dental Group', address: '9906 Kennerly Road, Saint Louis, Missouri, 63128', specialty: 'General Dentist', referral_count: 158, birthday: 'Jul 6th', last_referral: '11/03/2025', claimed: false },
    { id: 4, name: 'Dr. Justin Garner', phone: '(314) 892-2000', email: 'jgarner@NikodemDENTAL.COM', practice: 'Nikodem Dental', address: '4337 Butler Hill Rd, Ste G, Saint Louis, Missouri, 63128', specialty: 'General Dentist', referral_count: 114, last_referral: '10/23/2025' },
    { id: 5, name: 'Dr. Elizabeth Fassler', phone: '(636) 937-5118', email: 'fasbeth@earthlink.net', practice: 'Elizabeth Fassler', address: '412 West Main St, Festus, Missouri, 63028', specialty: 'General Dentist', referral_count: 100, birthday: 'Apr 2nd', last_referral: '10/30/2025' },
    { id: 6, name: 'Dr. Ryan Nikodem', phone: '(636) 937-9133', email: 'ryan.nikodem@gmail.com', practice: 'Ryan Nikodem', address: '1121 West Gannon Drive, Festus, Missouri, 63028', specialty: 'General Dentist', referral_count: 81, birthday: 'Mar 22nd', last_referral: '10/13/2025' },
    { id: 7, name: 'Dr. Janna Routh', phone: '(573) 327-8010', email: 'reception@upwardsmiles.com', practice: 'Upward Smiles', address: '60 N Creek Dr., Festus, Missouri, 63028', specialty: 'General Dentist', referral_count: 79, last_referral: '09/11/2025' }
  ];

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/providers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success && data.providers.length > 0) {
        setProviders(data.providers);
      } else {
        // Use default providers if none in database
        setProviders(defaultProviders);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      // Fallback to default providers
      setProviders(defaultProviders);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProvider = async () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.practice) {
      alert('Please fill in required fields: Name, Email, Phone, Practice');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/providers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        alert('Provider added successfully!');
        setShowAddModal(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          practice: '',
          address: '',
          specialty: 'General Dentist',
          birthday: '',
          notes: ''
        });
        fetchProviders();
      } else {
        alert('Failed to add provider');
      }
    } catch (error) {
      console.error('Error adding provider:', error);
      alert('Error adding provider');
    }
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const csv = event.target.result;
        const lines = csv.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        
        let uploadedCount = 0;
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          
          const values = lines[i].split(',').map(v => v.trim());
          const providerData = {
            name: values[headers.indexOf('Name')] || values[headers.indexOf('name')] || '',
            email: values[headers.indexOf('Email')] || values[headers.indexOf('email')] || '',
            phone: values[headers.indexOf('Phone')] || values[headers.indexOf('phone')] || '',
            practice: values[headers.indexOf('Practice')] || values[headers.indexOf('practice')] || '',
            address: values[headers.indexOf('Address')] || values[headers.indexOf('address')] || '',
            specialty: values[headers.indexOf('Specialty')] || values[headers.indexOf('specialty')] || 'General Dentist',
            birthday: values[headers.indexOf('Birthday')] || values[headers.indexOf('birthday')] || '',
            notes: ''
          };

          if (providerData.name && providerData.email) {
            try {
              await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/providers`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(providerData)
              });
              uploadedCount++;
            } catch (err) {
              console.error('Error uploading provider:', providerData.name, err);
            }
          }
        }

        alert(`Successfully imported ${uploadedCount} providers from CSV`);
        setShowUploadModal(false);
        fetchProviders();
      } catch (error) {
        console.error('Error parsing CSV:', error);
        alert('Error parsing CSV file. Please check format.');
      }
    };
    reader.readAsText(file);
  };

  const filteredProviders = providers.filter(provider => {
    const matchesSearch = provider.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          provider.practice?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSpecialty = specialtyFilter === 'all' || provider.specialty === specialtyFilter;
    return matchesSearch && matchesSpecialty;
  });

  const specialties = ['all', 'General Dentist', 'Orthodontist', 'Periodontist', 'Endodontist', 'Oral Surgeon', 'Pediatric Dentist'];

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
            <Building2 className="h-8 w-8 mr-3 text-[#D4AF37]" />
            Provider Directory
          </h1>
          <p className="text-gray-600">Manage your network of referring providers</p>
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline"
            onClick={() => setShowUploadModal(true)}
          >
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
          <Button 
            className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
            onClick={() => setShowAddModal(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Provider
          </Button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search providers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={specialtyFilter} onValueChange={setSpecialtyFilter}>
          <SelectTrigger>
            <SelectValue placeholder="Filter by specialty" />
          </SelectTrigger>
          <SelectContent>
            {specialties.map(spec => (
              <SelectItem key={spec} value={spec}>
                {spec === 'all' ? 'All Specialties' : spec}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Providers Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProviders.map((provider) => (
          <Card key={provider.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg mb-1">{provider.name}</CardTitle>
                  <p className="text-sm text-gray-600 mb-2">{provider.practice}</p>
                  <Badge variant="outline">{provider.specialty}</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {provider.claimed === false && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-yellow-800">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      <span className="font-medium">Unclaimed Profile</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/claim-profile?id=${provider.id}`)}
                      className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B] text-xs"
                    >
                      Claim Profile
                    </Button>
                  </div>
                </div>
              )}
              {provider.claimed === true && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-3">
                  <div className="flex items-center text-sm text-green-800">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    <span className="font-medium">Verified Profile</span>
                  </div>
                </div>
              )}
              
              <div className="flex items-center text-sm text-gray-600">
                <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                {provider.phone}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                {provider.email}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                {provider.address}
              </div>
              <div className="flex items-center justify-between text-sm pt-3 border-t">
                <span className="text-gray-600">
                  <TrendingUp className="h-4 w-4 inline mr-1" />
                  {provider.referral_count || 0} referrals
                </span>
                {provider.last_referral && (
                  <span className="text-gray-600">Last: {provider.last_referral}</span>
                )}
              </div>
              {provider.birthday && (
                <div className="text-xs text-gray-500">Birthday: {provider.birthday}</div>
              )}
              <div className="mt-3 pt-3 border-t flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/doctors/${provider.id}`)}
                  className="flex-1"
                >
                  View Profile
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/doctors/${provider.id}/edit`)}
                  className="flex-1"
                >
                  Edit
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add Provider Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Add New Provider</h2>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Provider Name *</label>
                  <Input
                    placeholder="Dr. John Smith"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Practice Name *</label>
                  <Input
                    placeholder="Smith Dental Practice"
                    value={formData.practice}
                    onChange={(e) => setFormData({...formData, practice: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <Input
                    type="email"
                    placeholder="doctor@practice.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone *</label>
                  <Input
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Address</label>
                <Input
                  placeholder="123 Main St, City, State, ZIP"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Specialty</label>
                  <select
                    className="w-full px-3 py-2 border rounded-md"
                    value={formData.specialty}
                    onChange={(e) => setFormData({...formData, specialty: e.target.value})}
                  >
                    <option>General Dentist</option>
                    <option>Orthodontist</option>
                    <option>Periodontist</option>
                    <option>Endodontist</option>
                    <option>Oral Surgeon</option>
                    <option>Pediatric Dentist</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Birthday</label>
                  <Input
                    placeholder="May 4th"
                    value={formData.birthday}
                    onChange={(e) => setFormData({...formData, birthday: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Notes</label>
                <Textarea
                  placeholder="Internal notes about this provider..."
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>

              <div className="flex space-x-2 pt-4">
                <Button 
                  className="flex-1 bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
                  onClick={handleAddProvider}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Provider
                </Button>
                <Button 
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData({
                      name: '',
                      email: '',
                      phone: '',
                      practice: '',
                      address: '',
                      specialty: 'General Dentist',
                      birthday: '',
                      notes: ''
                    });
                  }} 
                  variant="outline" 
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Import Providers from CSV</h2>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-blue-900 mb-2">CSV Format Requirements:</h3>
                <p className="text-sm text-blue-800 mb-2">Your CSV file should have these columns:</p>
                <code className="text-xs bg-white p-2 rounded block">
                  Name, Email, Phone, Practice, Address, Specialty, Birthday
                </code>
                <p className="text-xs text-blue-700 mt-2">
                  First row should be headers. Each provider on a new line.
                </p>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <input
                  type="file"
                  id="csvUpload"
                  accept=".csv"
                  onChange={handleCSVUpload}
                  className="hidden"
                />
                <label htmlFor="csvUpload">
                  <Button as="span" variant="outline" className="cursor-pointer">
                    Select CSV File
                  </Button>
                </label>
                <p className="text-sm text-gray-500 mt-2">
                  Click to browse or drag and drop your CSV file
                </p>
              </div>

              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowUploadModal(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default ProviderDirectory;